import React, { useState, useContext } from 'react';
import Styled from 'styled';
import range from 'lodash/range';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client/react/hooks';

import { Form, Formik, FormikProps } from 'formik';

import { AlertContext } from 'lib/alert-context/alert-context';
import AvatarRulesStackView from './avatar-rules-stack-view/avatar-rules-stack-view';
import AvatarCropperStackView from './avatar-cropper-stack-view';

import { Site } from 'four-nets/site';
import Avatar from 'four-nets/avatar/avatar';
import { FourNetsApi } from 'four-nets/api/api';
import Input from 'four-nets/ui-v2/form/input/input';
import Select from 'four-nets/ui-v2/form/select/select';
import GeoSelect from 'four-nets/geo/geo-select/geo-select';
import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import arrowRight from 'four-nets/icons/svg/icon_arrow-right.svg';
import FileInput from 'four-nets/ui-v2/form/file-input/file-input';

import defaultStyles from './settings-profile.scss';
import DateSelect from '../../date-select/date-select';
import { SettingsUserProfileFragment } from './settings-user-profile.graphql';
import mutation, { UpdateUserSettingsMutation, UpdateUserSettingsMutationVariables } from './mutation.graphql';

export interface Props extends SettingsUserProfileFragment {
    styles: typeof defaultStyles;
}

interface Values {
    name: string;
    sex: string;
    day: string;
    month: string;
    year: string;
    age: string;
    county: string | undefined;
    country: string | undefined;
    city: string;
    aboutMe: string;
    avatar: Blob | undefined;
}

const SettingsProfil: React.FC<Props> = props => {
    const { styles } = props;

    if (!props.profile) {
        return null;
    }

    const profile = props.profile!;

    const [showAgeInsteadOfDatePicker, setShowAgeInsteadOfDatePicker] = useState(!Boolean(profile.birthDate));
    const [avatarSrc, setAvatarSrc] = useState<string | null>(profile.avatar && profile.avatar.src);
    const [avatarRulesOpen, setAvatarRulesOpen] = useState(false);
    const [cropperOpen, setCropperOpen] = useState<{ open: boolean; file: File | null }>({ open: false, file: null });

    const { fail } = useContext(AlertContext);

    const [updateUserSettings, { loading }] = useMutation<
        UpdateUserSettingsMutation,
        UpdateUserSettingsMutationVariables
    >(mutation, { notifyOnNetworkStatusChange: true });

    const currentYear = new Date().getFullYear();
    const userBirthDate = {
        age: String(profile.age),
        day: '1',
        month: '1',
        year: profile.age ? String(currentYear - profile.age) : '1980',
    };

    if (profile.birthDate) {
        const date = new Date(Date.parse(profile.birthDate));
        userBirthDate.day = String(date.getDate());
        userBirthDate.month = String(date.getMonth());
        userBirthDate.year = String(date.getFullYear());
    }

    return (
        <div>
            <AvatarRulesStackView open={avatarRulesOpen} onClose={() => setAvatarRulesOpen(false)} />
            <FormattedMessage id='PERSONAL_DATA'>{msg => <h3 className={styles.header}>{msg}</h3>}</FormattedMessage>
            <Formik
                initialValues={{
                    avatar: undefined, // The actual avatar that is shows to users is in 'avatarSrc' this is for deleting/updating avatar
                    sex: String(profile.sex) || '1',
                    city: profile.city || '',
                    name: profile.name || '',
                    aboutMe: profile.aboutMe || '',
                    county: (profile.county && profile.county.id) || undefined,
                    country: (profile.country && profile.country.id) || undefined,
                    ...userBirthDate,
                }}
                onSubmit={(values, { resetForm, setTouched }) => {
                    const variables = {
                        input: {
                            name: values.name,
                            city: values.city,
                            id: profile.id,
                            sex: Number(values.sex),
                            aboutMe: values.aboutMe,
                            avatar: values.avatar ? new File([values.avatar!], 'tommy wiseau') : values.avatar,
                        },
                    } as UpdateUserSettingsMutationVariables;

                    if (values.county && values.country) {
                        variables.input.countyId = values.county;
                    } else {
                        variables.input.countyId = values.county;
                        variables.input.countryId = values.country;
                    }

                    if (showAgeInsteadOfDatePicker) {
                        variables.input.age = Number(values.age);
                    } else {
                        const date = new Date();
                        date.setDate(Number(values.day));
                        date.setMonth(Number(values.month));
                        date.setFullYear(Number(values.year));
                        variables.input.birthDate = date.toISOString();
                    }

                    updateUserSettings({ variables })
                        .then(() => {
                            resetForm({ values, touched: {}, errors: {} });
                            setTouched({});
                        })
                        .catch(reason => {
                            fail!({
                                body: JSON.stringify(reason),
                                onAccept: () => window.location.reload(true),
                            });
                        });
                }}
            >
                {({ setFieldValue, isValid, dirty, values, isSubmitting, getFieldProps }: FormikProps<Values>) => (
                    <React.Fragment>
                        <AvatarCropperStackView
                            open={cropperOpen.open}
                            file={cropperOpen.file}
                            onClose={() => {
                                setCropperOpen({ open: false, file: null });
                            }}
                            onAction={(blob: Blob) => {
                                setFieldValue('avatar', blob);
                                setAvatarSrc(URL.createObjectURL(blob));
                            }}
                        />
                        <Form className={styles.form}>
                            <div className={styles.dividerDiv}>
                                <div className={styles.dividerLeft}>
                                    <label className={styles.label} htmlFor='name'>
                                        <FormattedMessage id='FIRSTNAME_AND_LASTNAME' />
                                    </label>
                                    <Input style={{ wrapper: styles.inputWrapper }} {...getFieldProps('name')} />
                                    <label className={styles.label} htmlFor='sex'>
                                        <FormattedMessage id='GENDER' />
                                    </label>
                                    <Select {...getFieldProps('sex')} multiple={false}>
                                        <FormattedMessage id='FEMALE'>
                                            {msg => <option value={1}>{msg}</option>}
                                        </FormattedMessage>
                                        <FormattedMessage id='MALE'>
                                            {msg => <option value={2}>{msg}</option>}
                                        </FormattedMessage>
                                    </Select>
                                </div>
                                <div>
                                    <div className={styles.panelFields}>
                                        <Avatar
                                            style={{ avatar: styles.avatar }}
                                            src={avatarSrc || undefined}
                                            linkify={false}
                                        />
                                        <div className={styles.avatarButtonWrapper}>
                                            {(profile && profile.avatar) || avatarSrc ? (
                                                <button
                                                    className={styles.avatarButton}
                                                    type='button'
                                                    onClick={() => {
                                                        setFieldValue('avatar', null);
                                                        setAvatarSrc(null);
                                                    }}
                                                >
                                                    <FormattedMessage id='DELETE_AVATAR' values={{ gender: true }} />
                                                </button>
                                            ) : (
                                                <span className={styles.avatarButtonLabel}>
                                                    <FormattedMessage id='SHOW_YOURSELF' />
                                                    <br />
                                                    <FormattedMessage id='WE_ARE_INTERESTED' />
                                                </span>
                                            )}
                                            <div className={styles.avatarInput}>
                                                <FileInput
                                                    style={{ fileInput: styles.fileInputSpan }}
                                                    onChange={event => {
                                                        if (
                                                            event &&
                                                            event.target &&
                                                            event.target.files &&
                                                            event.target.files.length === 1
                                                        ) {
                                                            setCropperOpen({ open: true, file: event.target.files[0] });
                                                        }
                                                    }}
                                                    multiple={false}
                                                    accept={'image/*'}
                                                />
                                            </div>
                                            <button
                                                className={styles.avatarRulesButton}
                                                onClick={() => {
                                                    setAvatarRulesOpen(true);
                                                }}
                                                type='button'
                                            >
                                                <span className={styles.avatarRulesButtonLabel}>
                                                    <FormattedMessage id='AVATAR_RULES' />
                                                </span>
                                                <SVGIcon src={arrowRight} className={styles.avatarRulesIcon} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.dateWrapper}>
                                {showAgeInsteadOfDatePicker ? (
                                    <label>
                                        <FormattedMessage id='AGE_COLON'>
                                            {msg => <span className={styles.labelText}>{msg}</span>}
                                        </FormattedMessage>
                                        <Select
                                            style={{ container: styles.selectWrapper }}
                                            {...getFieldProps('age')}
                                            multiple={false}
                                        >
                                            <option value=''>--</option>
                                            {range(15, 101).map((index: number) => {
                                                return (
                                                    <option key={index} value={index}>
                                                        {index}
                                                    </option>
                                                );
                                            })}
                                        </Select>
                                    </label>
                                ) : (
                                    <DateSelect
                                        dayField={getFieldProps('day')}
                                        monthField={getFieldProps('month')}
                                        yearField={getFieldProps('year')}
                                    />
                                )}
                                <button
                                    onClick={() => setShowAgeInsteadOfDatePicker(!showAgeInsteadOfDatePicker)}
                                    type='button'
                                    className={styles.ageButton}
                                >
                                    {showAgeInsteadOfDatePicker ? (
                                        <FormattedMessage id='FILL_OUT_DATE' />
                                    ) : (
                                        <FormattedMessage id='FILL_OUT_ONLY_AGE' />
                                    )}
                                </button>
                            </div>
                            <div className={styles.geoWrapper}>
                                <div className={styles.countyWrapper}>
                                    <label>
                                        <FormattedMessage id='BAZAAR_COUNTY'>
                                            {msg => <span className={styles.labelText}>{msg}</span>}
                                        </FormattedMessage>
                                        <GeoSelect
                                            style={{ wrapper: styles.geoSelectWrapper }}
                                            {...getFieldProps('county')}
                                            countyId={values.county}
                                            countryId={values.country}
                                            allowCountrySelection={true}
                                            onChange={(selectedCountyID: string, selectedCountryID: string) => {
                                                setFieldValue('county', selectedCountyID);
                                                setFieldValue('country', selectedCountryID);
                                            }}
                                        />
                                    </label>
                                </div>
                                <div className={styles.cityWrapper}>
                                    <label className={styles.label} htmlFor='city'>
                                        <FormattedMessage id='WHERE_LIVING_CITY' />
                                    </label>
                                    <Input style={{ wrapper: styles.inputWrapper }} {...getFieldProps('city')} />
                                </div>
                            </div>
                            <label className={styles.label} htmlFor='aboutMe'>
                                <FormattedMessage id='ABOUT_ME' values={{ gender: true }} />
                            </label>
                            <TextArea
                                style={{
                                    textareaDiv: styles.textAreaTextareaDiv,
                                    textAreaWrapper: styles.textAreaWapper,
                                }}
                                {...getFieldProps('aboutMe')}
                                onChange={e => setFieldValue('aboutMe', e.target.value)}
                                maxLength={500}
                            />
                            <div className={styles.submitButtonRow}>
                                <button disabled={loading || !dirty} className={styles.submitButton}>
                                    <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                                </button>
                            </div>
                        </Form>
                    </React.Fragment>
                )}
            </Formik>
        </div>
    );
};

SettingsProfil.contextTypes = {
    site: PropTypes.instanceOf(Site),
    api: PropTypes.instanceOf(FourNetsApi),
};

export default Styled(defaultStyles)(SettingsProfil);
